import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";

import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import FormInputField from "@/components/lib/from-input-field";

import useAuth from "@/hooks/useAuth";
import {
  addOnlineOrderDiscountOfferApi,
  editOnlineOrderDiscountOfferApi,
} from "@/api/onlineOrderDiscountOffer";

const offerSchema = z.object({
  name: z.string().min(1, "Offer name is required"),
  description: z.string().optional(),
  status: z.enum(["active", "inactive"]),
  spend_threshold: z.coerce
    .number({ invalid_type_error: "Spend threshold must be a number" })
    .min(0, "Spend threshold must be at least 0"),
  discount_type: z.enum(["percentage", "fixed"]),
  discount_value: z.coerce
    .number({ invalid_type_error: "Discount value must be a number" })
    .min(0, "Discount value must be at least 0"),
});

export default function AddOnlineOrderDiscountOffer() {
  const navigate = useNavigate();
  const location = useLocation();
  const updateItem = location.state;
  const { type } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(offerSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      status: "active",
      spend_threshold: 0,
      discount_type: "percentage",
      discount_value: "",
    },
  });

  // Pre-fill form if editing
  useEffect(() => {
    if (updateItem) {
      form.reset({
        name: updateItem.name || "",
        description: updateItem.description || "",
        status: updateItem.status || "active",
        spend_threshold: updateItem.spend_threshold || 0,
        discount_type: updateItem.discount_type || "percentage",
        discount_value: updateItem.discount_value !== undefined ? String(updateItem.discount_value) : "",
      });
    }
  }, [updateItem, form]);

  // Form Submission
  const handleSubmitOffer = async (data) => {
    setLoading(true);

    const payload = {
      client_id: user?.client_id || 1,
      restaurant_id: user?.restaurant_id || 1,
      name: data.name,
      description: data.description || "",
      status: data.status,
      spend_threshold: data.spend_threshold,
      discount_type: data.discount_type,
      discount_value: data.discount_value,
    };

    try {
      let res;
      if (updateItem && updateItem.id) {
        res = await editOnlineOrderDiscountOfferApi(payload, updateItem.id);
      } else {
        res = await addOnlineOrderDiscountOfferApi(payload);
      }

      if (res.status === "success") {
        toast.success(
          updateItem
            ? "Offer updated successfully!"
            : "Offer created successfully!"
        );
        navigate("/offer/online-order-get-discount");
      } else {
        toast.error(res.message || "Failed to save offer.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout2
      pageName={{ title: updateItem ? "Update Offer" : "Add Online Order Discount Offer" }}
      paths={[
        { name: "Loyalty", path: "/loyalty" },
        { name: "Offers", path: "/offer/online-order-get-discount" },
        { name: "Online Order Discount", path: "/offer/online-order-get-discount" },
        { name: updateItem ? "Update Offer" : "Add New Offer" },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}
    >
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-cwhite text-xl">
              {updateItem ? "Update Online Order Discount Offer" : "Create Online Order Discount Offer"}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate("/offer/online-order-get-discount")}
              className="border-neutral-700 text-cwhite hover:bg-cblack3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </CardHeader>
          <Separator className="bg-neutral-800" />
          <CardContent className="mt-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmitOffer)}
                className="flex flex-col gap-6"
              >
                {/* Offer Name */}
                <FormInputField
                  name="name"
                  style="bg-cblack3 border-neutral-700"
                  placeholder="Enter offer name (e.g. 10% off online orders)"
                  form={form}
                  type="text"
                  label="Offer Name"
                  required={true}
                />

                {/* Description */}
                <FormInputField
                  name="description"
                  style="bg-cblack3 border-neutral-700"
                  placeholder="Enter offer description"
                  form={form}
                  type="text"
                  label="Description"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Spend Threshold */}
                  <FormInputField
                    name="spend_threshold"
                    style="bg-cblack3 border-neutral-700"
                    placeholder="Enter threshold amount (e.g. 20)"
                    form={form}
                    type="number"
                    label="Spend Threshold (£)"
                    required={true}
                  />

                  {/* Status */}
                  <FormInputField
                    name="status"
                    style="bg-cblack3 border-neutral-700"
                    placeholder="Select status"
                    form={form}
                    type="select"
                    selectOption={[
                      { label: "Active", value: "active" },
                      { label: "Inactive", value: "inactive" },
                    ]}
                    label="Status"
                    required={true}
                  />
                </div>

                <Separator className="bg-neutral-800 my-2" />

                {/* SECTION 2: DISCOUNT REWARD DETAILS */}
                <div className="border border-neutral-800 p-5 rounded-lg bg-cblack3">
                  <h2 className="font-bold text-lg text-cwhite mb-4">Discount Reward</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Discount Type */}
                    <FormInputField
                      name="discount_type"
                      style="bg-cblack3 border-neutral-700"
                      form={form}
                      type="select"
                      selectOption={[
                        { label: "Percentage (%)", value: "percentage" },
                        { label: "Fixed Amount (£)", value: "fixed" },
                      ]}
                      label="Discount Type"
                      required={true}
                    />

                    {/* Discount Value */}
                    <FormInputField
                      name="discount_value"
                      style="bg-cblack3 border-neutral-700"
                      placeholder={form.watch("discount_type") === "fixed" ? "e.g. 5.00" : "e.g. 10"}
                      form={form}
                      type="number"
                      label="Discount Value"
                      required={true}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/offer/online-order-get-discount")}
                    className="border-neutral-700 text-cwhite hover:bg-cblack3"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-cyellow text-black hover:bg-yellow-500 font-bold px-8"
                  >
                    {loading
                      ? "Saving..."
                      : updateItem
                      ? "Update Offer"
                      : "Create Offer"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout2>
  );
}
